// frontend/App.js
import './assets/css/App.css';
import { getSubdomain } from './config/subdomainResolver';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import initialTheme from './theme/theme';
import { lazy, Suspense, useState, useEffect, useContext, useCallback } from 'react';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { SubscriptionProvider, SubscriptionContext } from './contexts/SubscriptionContext';
import { SearchProvider } from './contexts/SearchContext';
import { NotificationProvider } from './components/notifications/NotificationProvider';
import TermsModal from './components/terms/TermsModal';
import CookieConsent from './components/cookies/CookieConsent';
import Loader from './components/loader/Loader';
import WhatsAppButton from './components/whatsapp/WhatsAppButton';
import { APP_CONFIG } from './config/api';

// ── Code-split every route-level component ──────────────────────────────────
// Each lazy() call becomes a separate chunk loaded only when that route is visited.
const AuthLayout   = lazy(() => import('./layouts/auth'));
const AdminLayout  = lazy(() => import('./layouts/admin'));
const RTLLayout    = lazy(() => import('./layouts/rtl'));
const PageNotFound = lazy(() => import('./components/error/PageNotFound'));
const TrialExpiredPage        = lazy(() => import('./views/subscription/TrialExpiredPage'));
const PricingPage             = lazy(() => import('./views/auth/seoPages/PricingPage'));
const AlternativesLandingPage = lazy(() => import('./views/auth/seoPages/alternatives/AlternativesLandingPage'));
const GreytHRAlternativePage  = lazy(() => import('./views/auth/seoPages/alternatives/GreytHRAlternativePage'));
const ZohoPeopleAlternativePage   = lazy(() => import('./views/auth/seoPages/alternatives/ZohoPeopleAlternativePage'));
const PeoplehumAlternativePage    = lazy(() => import('./views/auth/seoPages/alternatives/PeoplehumAlternativePage'));
const HrmsForStartupsPage         = lazy(() => import('./views/auth/seoPages/industries/HrmsForStartupsPage'));
const HrmsForRemoteTeamsPage      = lazy(() => import('./views/auth/seoPages/industries/HrmsForRemoteTeamsPage'));
const HrmsForItCompaniesPage      = lazy(() => import('./views/auth/seoPages/industries/HrmsForItCompaniesPage'));
const HrmsForStartupsBlog         = lazy(() => import('./views/auth/seoPages/blog/HrmsForStartupsBlog'));
const HrSoftwareForRemoteTeamsBlog = lazy(() => import('./views/auth/seoPages/blog/HrSoftwareForRemoteTeamsBlog'));
const BestFreeHrAppsBlog = lazy(() => import('./views/auth/seoPages/blog/BestFreeHrAppsBlog'));
const HrmsSystemGuideBlog = lazy(() => import('./views/auth/seoPages/blog/HrmsSystemGuideBlog'));
const NewLabourCodes2026Blog = lazy(() => import('./views/auth/seoPages/blog/NewLabourCodes2026Blog'));
const HrSoftwareForSmallBusinessPage = lazy(() => import('./views/auth/seoPages/HrSoftwareForSmallBusinessPage'));
const FreeAttendanceTrackerPage      = lazy(() => import('./views/auth/seoPages/FreeAttendanceTrackerPage'));
const FaceIdAttendancePage           = lazy(() => import('./views/auth/seoPages/FaceIdAttendancePage'));
const FreeEmployeeAttendanceSoftwarePage = lazy(() => import('./views/auth/seoPages/FreeEmployeeAttendanceSoftwarePage'));
const FreeEmployeeMonitoringSoftwarePage = lazy(() => import('./views/auth/seoPages/FreeEmployeeMonitoringSoftwarePage'));
const EmployeeManagementSystemPage       = lazy(() => import('./views/auth/seoPages/EmployeeManagementSystemPage'));

if (APP_CONFIG.APP_ENV === 'production') {
  console.log = () => { };
  console.warn = () => { };
  console.error = () => { };
}

const ProtectedRoute = ({ children, requiredRole }) => {
  const { auth, loading, apiCall, refreshAuth } = useContext(AuthContext);
  // Source of truth for billing access — auto-updates after Subscribe / Cancel
  // because SubscriptionContext.refresh() triggers a re-render here.
  const {
    subscription,
    derived,
    loading: subLoading,
    fetchedAt: subFetchedAt,
    error: subError,
  } = useContext(SubscriptionContext);
  const location = useLocation();
  const [showTerms, setShowTerms] = useState(false);
  const [checkingTerms, setCheckingTerms] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const checkTermsAcceptance = useCallback(async () => {
    if (auth?.role === 'admin') {
      setTermsChecked(true);
      return;
    }

    try {
      setCheckingTerms(true);
      const data = await apiCall('/api/terms/current');

      if (data.hasTerms && data.needsAcceptance) {
        setShowTerms(true);
      } else {
        setShowTerms(false);
      }
    } catch (error) {
      console.error('Error checking terms:', error);
      setShowTerms(false);
    } finally {
      setCheckingTerms(false);
      setTermsChecked(true);
    }
  }, [apiCall, auth]);

  useEffect(() => {
    if (!loading && auth && !termsChecked) {
      checkTermsAcceptance();
    }
  }, [loading, auth, termsChecked, checkTermsAcceptance]);

  const handleTermsAccepted = async () => {
    setShowTerms(false);
    setTermsChecked(false);
    await refreshAuth();
  };

  if (loading) {
    return (
      <Loader
        fullScreen={true}
        message="Loading..."
        size={200}
      />
    );
  }

  if (!auth) {
    const redirectTo = location.pathname === '/' ? '/index' : '/sign-in';
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (showTerms || (checkingTerms && !termsChecked)) {
    return <TermsModal isOpen={true} onAccept={handleTermsAccepted} apiCall={apiCall} />;
  }

  // ── Subscription / trial gate ─────────────────────────────────────────────
  // Reads from SubscriptionContext so a successful payment (which calls
  // refresh()) automatically lets the user back into the app — no page reload.
  // Fail-open on any error so a billing API outage doesn't lock the whole app.
  if (termsChecked && !showTerms) {
    if (!subFetchedAt && subLoading) {
      return <Loader fullScreen={true} message="Checking access..." size={200} />;
    }
    if (subFetchedAt && !subError && derived && !derived.hasAccess) {
      const trialPeriodInDays = subscription?.trialStart && subscription?.trialEnd
        ? Math.ceil((new Date(subscription.trialEnd) - new Date(subscription.trialStart)) / (1000 * 60 * 60 * 24))
        : 60;
      const trialData = {
        hasAccess: false,
        accessType: derived.accessType || subscription?.status || 'expired',
        daysRemaining: derived.daysRemaining || 0,
        trialStartDate: subscription?.trialStart || null,
        trialEndDate: derived.endsAt || subscription?.trialEnd || subscription?.currentPeriodEnd || null,
        trialPeriodInDays,
        subscriptionStatus: subscription?.status,
      };
      return <TrialExpiredPage trialData={trialData} />;
    }
  }

  if (requiredRole && auth.role !== requiredRole && auth.role !== 'admin') {
    return <Navigate to="/home" replace />;
  }

  if (auth.status === 'inactive' && auth.role !== 'admin') {
    const allowedForInactive = ['/home', '/profile', '/payslip', '/company-feedback'];

    if (!allowedForInactive.some(path => location.pathname.includes(path))) {
      return <Navigate to="/home" replace />;
    }
  }

  return children;
};

const CheckAuth = ({ element }) => {
  const { auth, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <Loader
        fullScreen={true}
        message="Checking authentication..."
        size={200}
      />
    );
  }

  if (auth) {
    // ── Redirect to company slug URL ─────────────────────────────────────────
    const companySlug = auth.companySlug;
    const hostname = window.location.hostname;
    const isLocal = hostname.includes('localhost') || hostname.includes('127.0.0.1');

    if (companySlug && !isLocal) {
      const currentSubdomain = getSubdomain();
      if (currentSubdomain !== companySlug) {
        // Transfer session securely and remove from current domain
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        if (accessToken && refreshToken) {
            const transferData = { accessToken, refreshToken, companySlug };
            document.cookie = `auth_transfer=${encodeURIComponent(JSON.stringify(transferData))}; max-age=60; domain=.empworkspace.com; path=/;`;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('companySlug');
        }
        window.location.href = `${window.location.protocol}//${companySlug}.empworkspace.com`;
        return null; // prevent rendering while the browser navigates away
      }
    }

    // Localhost or already on the correct subdomain — navigate in-app
    const from = location.state?.from?.pathname || '/home';
    return <Navigate to={from} replace />;
  }

  return element;
};

export default function Main() {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const navigate = useNavigate();
  const location = useLocation();
  const subdomain = getSubdomain();

  useEffect(() => {
    if (!subdomain) return;

    // On a tenant subdomain, only public marketing/landing pages should be redirected to /home.
    // Auth pages (/sign-in, /forgot-password, etc.) and ALL app routes must be left alone.
    // Using a blocklist of known marketing routes is safer than an allowlist of app routes.
    const marketingRoutes = [
      '/index',
      '/hr-software',
      '/employee-portal',
      '/attendance-management',
      '/payroll-management',
      '/hr-analytics',
      '/employee-self-service',
      '/performance-management',
      '/pricing',
      '/alternatives',
      '/hrms-for-startups',
      '/hrms-for-remote-teams',
      '/hrms-for-it-companies',
      '/hr-software-for-small-business',
      '/free-attendance-tracker',
      '/face-id-attendance-app',
      '/free-employee-attendance-software',
      '/free-employee-monitoring-software',
      '/employee-management-system',
      '/blog',
      '/download',
      '/privacy-policy',
    ];

    const isMarketingRoute = marketingRoutes.some(
      r => location.pathname === r || location.pathname.startsWith(r + '/')
    );

    // Only redirect root path and known marketing pages.
    if (location.pathname === '/' || isMarketingRoute) {
      navigate('/home', { replace: true });
    }
  }, [subdomain, location.pathname, navigate]);

  return (
    <ChakraProvider theme={currentTheme}>
      <AuthProvider>
        <SubscriptionProvider>
        <SearchProvider> {/* Wrap with SearchProvider */}
          <NotificationProvider>
            <Suspense fallback={<Loader fullScreen={true} message="Loading..." size={200} />}>
            <Routes>
                <Route path="/sign-in" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/*" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/get-started" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/forgot-password" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/reset-password" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/reportdata" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/leave-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/payslip-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/career-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/announcement-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/policy-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/invoice-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/it-industry" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/finance-industry" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/index/faq" element={<CheckAuth element={<AuthLayout />} />} />
                {/* ── SEO Landing Pages ── */}
                <Route path="/hr-software" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/employee-portal" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/attendance-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/payroll-management" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/hr-analytics" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/employee-self-service" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/performance-management" element={<CheckAuth element={<AuthLayout />} />} />
                {/* ── Pricing ── */}
                <Route path="/pricing" element={<CheckAuth element={<PricingPage />} />} />
                {/* ── Alternatives / Comparison pages ── */}
                <Route path="/alternatives" element={<CheckAuth element={<AlternativesLandingPage />} />} />
                <Route path="/alternatives/greythr-alternative" element={<CheckAuth element={<GreytHRAlternativePage />} />} />
                <Route path="/alternatives/zoho-people-alternative" element={<CheckAuth element={<ZohoPeopleAlternativePage />} />} />
                <Route path="/alternatives/peoplehum-alternative" element={<CheckAuth element={<PeoplehumAlternativePage />} />} />
                {/* ── Industry solution pages ── */}
                <Route path="/hrms-for-startups" element={<CheckAuth element={<HrmsForStartupsPage />} />} />
                <Route path="/hrms-for-remote-teams" element={<CheckAuth element={<HrmsForRemoteTeamsPage />} />} />
                <Route path="/hrms-for-it-companies" element={<CheckAuth element={<HrmsForItCompaniesPage />} />} />
                {/* ── Long-tail SEO landing pages (Phase: P2) ── */}
                <Route path="/hr-software-for-small-business" element={<CheckAuth element={<HrSoftwareForSmallBusinessPage />} />} />
                <Route path="/free-attendance-tracker" element={<CheckAuth element={<FreeAttendanceTrackerPage />} />} />
                <Route path="/face-id-attendance-app" element={<CheckAuth element={<FaceIdAttendancePage />} />} />
                <Route path="/free-employee-attendance-software" element={<CheckAuth element={<FreeEmployeeAttendanceSoftwarePage />} />} />
                <Route path="/free-employee-monitoring-software" element={<CheckAuth element={<FreeEmployeeMonitoringSoftwarePage />} />} />
                <Route path="/employee-management-system" element={<CheckAuth element={<EmployeeManagementSystemPage />} />} />
                {/* ── Blog Pages ── */}
                <Route path="/blog" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/blog/best-hr-software" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/blog/employee-portal-benefits" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/blog/attendance-management-guide" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/blog/delete-account-data" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/blog/hrms-for-startups" element={<CheckAuth element={<HrmsForStartupsBlog />} />} />
                <Route path="/blog/hr-software-for-remote-teams" element={<CheckAuth element={<HrSoftwareForRemoteTeamsBlog />} />} />
                <Route path="/blog/best-free-hr-apps" element={<CheckAuth element={<BestFreeHrAppsBlog />} />} />
                <Route path="/blog/hrms-system-guide" element={<CheckAuth element={<HrmsSystemGuideBlog />} />} />
                <Route path="/blog/new-labour-codes-2026" element={<CheckAuth element={<NewLabourCodes2026Blog />} />} />
                <Route path="/download" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/privacy-policy" element={<CheckAuth element={<AuthLayout />} />} />
                <Route path="/404" element={<PageNotFound />} />
                <Route
                  path="/rtl/*"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <RTLLayout theme={currentTheme} setTheme={setCurrentTheme} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/home" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="*"
                  element={
                    <ProtectedRoute>
                      <AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />
                    </ProtectedRoute>
                  }
                />
            </Routes>
            </Suspense>
            <CookieConsent />
            <WhatsAppButton />
          </NotificationProvider>
        </SearchProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}